export type User = {
  id: string;
  email: string;
  name: string;
  tenant_id: string;
  tenant_name?: string;
  role: string;
};

export type PlatformPlanPrice = {
  id: string;
  currency: string;
  currency_exponent: number;
  amount_minor: number;
  billing_interval: string;
  interval_count: number;
  effective_from: string | null;
  effective_to: string | null;
};

export type PlatformPlan = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  status: string;
  version: number;
  metadata: {
    label?: string;
    tagline?: string;
    highlighted?: boolean;
    ai_credits?: number;
    limits?: Record<string, number>;
    included?: string[];
    support?: string;
    overage?: string;
    [key: string]: unknown;
  };
  prices: PlatformPlanPrice[];
};

export type PlatformBilling = {
  subscription: null | {
    id: string;
    status: string;
    current_period_start: string | null;
    current_period_end: string | null;
    code: string;
    name: string;
    amount_minor: number;
    currency: string;
    currency_exponent: number;
    billing_interval: string;
  };
  credits: {
    balance_minor: number;
    reserved_minor: number;
    available_minor: number;
    version: number;
    account_exists: boolean;
  };
  invoices: Array<{
    id: string;
    number: string;
    status: string;
    currency: string;
    currency_exponent: number;
    total_minor: number;
    due_at: string | null;
    paid_at: string | null;
    created_at: string;
  }>;
};

export type Theme = {
  id: string;
  slug: string;
  name: string;
  description: string;
  category: string;
  tags: string;
  version?: number;
  score?: number;
};

export type PremiumTemplate = {
  id: string;
  slug: string;
  name: string;
  description: string;
  category: string;
  status: string;
  version: string;
  capabilities: string[];
  locales: string[];
  preview?: { demoUrl?: string; buildHash?: string; status?: string; screenshots?: Array<{ device: string; url: string }> };
  qa?: { status?: string; reviewer?: string | null; lastRunAt?: string };
};

export type Project = {
  id: string;
  slug: string;
  name: string;
  status: string;
  theme_id?: string | null;
  updated_at?: string;
  created_at?: string;
};

export type ProjectResourceBinding = {
  id: string;
  type: "product_catalog" | "commerce_store" | "premium_template" | "facebook_page" | "domain" | "media_library";
  resource_id: string;
  status: "active" | "disconnected" | "error";
  selected_scope: Record<string, unknown>;
  permissions: Record<string, boolean>;
  configuration: Record<string, unknown>;
  products?: Array<{ id: string; sku: string; name: string; brand: string; category: string; price_amount: number; currency: string; stock_quantity: number; status: string; image_url: string }>;
  store?: CommerceStore | null;
  template?: PremiumTemplate | null;
};

export type ConnectProjectResourcesInput = {
  template_id: string;
  store_id: string;
  catalog: { project_id: string; product_ids: string[] };
};

export type Generation = {
  id: string;
  status: string;
  stage: string;
  prompt: string;
  provider?: string | null;
  model?: string | null;
  plan_json?: string | null;
  result_json?: string | null;
  created_at?: string;
};

export type GenerationStep = {
  id: string;
  step_key: string;
  status: "queued" | "running" | "completed" | "failed" | "canceled";
  input_json?: string | null;
  output_json?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
};

export type ProjectPage = {
  id: string;
  slug: string;
  title: string;
  position: number;
  schema_json: string;
  status: string;
  version: number;
};

export type ProductStatus = "draft" | "published" | "archived";
export type CatalogProduct = {
  id: string;
  sku: string;
  slug: string;
  name: string;
  brand: string;
  category: string;
  gender: string;
  shortDescription: string;
  description: string;
  priceAmount: number;
  compareAtAmount: number | null;
  currency: string;
  stockQuantity: number;
  status: ProductStatus;
  imageUrl: string;
  imageAlt: string;
  variants: Record<string, unknown>;
  specifications: Record<string, unknown>;
  seo: Record<string, unknown>;
  tags: string[];
  createdAt?: string;
  updatedAt?: string;
};
export type CatalogProductInput = Omit<CatalogProduct, "id" | "createdAt" | "updatedAt">;

export type ProjectPageRevision = {
  id: string;
  page_id: string;
  revision_number: number;
  title: string;
  slug: string;
  position: number;
  schema_json: string;
  change_source: string;
  created_by?: string | null;
  created_at: string;
};

export type ProjectPreview = {
  id: string;
  url: string;
  artifact_sha256: string;
  revision_map: Record<string, number>;
  revision_id?: string | null;
  revision_number?: number | null;
};

export type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
};

type Envelope<T> = { data: T; request_id?: string };

type ApiErrorPayload = {
  error?: { code?: string; message?: string };
};

export type DomainReadiness = {
  commerce_enabled: boolean;
  registration_enabled: boolean;
  connect_enabled: boolean;
  d1_schema_ready: boolean;
  persisted_spend_policy_configured: boolean;
  registrar_configured: boolean;
  registrar_provider: string | null;
  verified_payment_service_configured: boolean;
  custom_hostname_service_configured: boolean;
  explicit_cost_caps_configured: boolean;
  blockers: string[];
};

export type DomainInventoryItem = {
  id: string;
  domain_ascii: string;
  domain_display: string;
  source_kind: "direct_purchase" | "plan_included" | "existing_domain";
  ownership_status: "unverified" | "pending" | "verified" | "failed";
  service_status: string;
  expires_at: string | null;
  ownership_verified_at: string | null;
  assignment_id?: string | null;
  project_id?: string | null;
  assignment_status?: string | null;
  hostname_status?: string | null;
  certificate_validation_status?: string | null;
  ssl_status?: string | null;
  ssl_validation_status?: string | null;
};

export type DomainSearchResult = {
  domain_ascii: string;
  availability_status: "available" | "unavailable" | "premium" | "unknown";
  premium: boolean;
  registration_minor: number | null;
  renewal_minor: number | null;
  currency: string | null;
  currency_exponent: number | null;
  registration_years: number;
  quote_expires_at?: string | null;
};

export type DomainConnectResult = {
  domain: Pick<DomainInventoryItem, "id" | "domain_ascii" | "domain_display" | "source_kind" | "ownership_status" | "service_status">;
  challenge: { id?: string; type?: "TXT"; name?: string; value?: string; ttl?: number; expires_in_seconds?: number; challenge_record_name?: string; expires_at?: string; status?: string; value_available?: boolean };
  next_step?: string;
};

export type DomainProviderAdminConfig = {
  provider_slug: "openprovider";
  environment: "sandbox";
  api_base_url_candidate: string;
  api_version: "v1";
  endpoint_status: "awaiting_written_vendor_confirmation";
  authentication_scheme: string;
  config_version: number;
  credentials_configured: boolean;
  credentials_updated_at: string | null;
  credential_key_version: string | null;
  encryption_key_format_valid: boolean;
  can_save_credentials: boolean;
  evidence_references: { sandbox_v1: string | null; no_charge: string | null; price_operations: string | null };
  request_limits: { timeout_ms: number; max_response_bytes: number; max_tld_pages: number };
  effective_capabilities: { tld_read: false; availability_read: false; price_read: false; registration: false; renewal: false; provider_calls_enabled: false };
  integration_status: "disabled_pending_vendor_and_release_gates";
  created_at: string | null;
  updated_at: string | null;
};

export type CreativeProjectKind = "image" | "document" | "product_slideshow" | "presentation" | "video" | "audio" | "marketing" | "workflow";

export type CreativeAiOperationCapability = {
  operation: string;
  available: boolean;
  candidate_model: string;
  candidate_models: string[];
  official_limits: {
    reference_images: { minimum: number; maximum: number; maximum_width_exclusive: number; maximum_height_exclusive: number };
    output: { minimum_width: number; maximum_width: number; minimum_height: number; maximum_height: number };
    steps: number;
    prompt: { minimum_length: number; maximum_length: number };
  };
  blockers: string[];
};

export type CreativeAiCapabilities = {
  contract_version: string;
  operations: CreativeAiOperationCapability[];
};

export type CreativeDocumentSnapshot = {
  schema_version: 1;
  kind: "document";
  state: {
    body: string;
    asset_ids: string[];
  };
};

export type CreativeSnapshot = CreativeDocumentSnapshot | {
  schema_version: number;
  kind?: string;
  state?: Record<string, unknown>;
  [key: string]: unknown;
};

export type CreativeProject = {
  id: string;
  kind: CreativeProjectKind;
  title: string;
  status: "draft" | "archived" | string;
  current_revision_number: number;
  created_at: string;
  updated_at: string;
};

/** A descriptor is metadata only. It intentionally has no signed URL token. */
export type CreativeAttachmentDescriptor = {
  id?: string;
  asset_id?: string;
  filename?: string;
  asset_type?: string;
  mime_type?: string;
  content_url?: string;
  thumbnail_url?: string;
  width?: number | null;
  height?: number | null;
  duration_ms?: number | null;
  updated_at?: string;
};

export type CreativeRevision = {
  id: string;
  revision_number: number;
  schema_version: number;
  content_hash_sha256: string;
  snapshot: CreativeSnapshot;
  change_source?: string;
  created_by?: string | null;
  created_at?: string;
  /** Backends may expose attached safe descriptors under either supported field. */
  assets?: CreativeAttachmentDescriptor[];
  attachments?: CreativeAttachmentDescriptor[];
  asset_descriptors?: CreativeAttachmentDescriptor[];
};

export type CreativeRevisionSummary = Omit<CreativeRevision, "snapshot" | "assets" | "attachments" | "asset_descriptors">;

export type CreativeAssetDescriptor = {
  id: string;
  project_id?: string | null;
  asset_type: string;
  source?: string;
  status?: string;
  filename?: string;
  mime_type?: string;
  byte_size?: number;
  sha256_hex?: string;
  width?: number | null;
  height?: number | null;
  duration_ms?: number | null;
  created_at?: string;
  updated_at?: string;
  /** Authenticated content endpoints, never signed-token URLs. */
  content_url?: string;
  thumbnail_url?: string;
};

export class ApiRequestError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = "ApiRequestError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (typeof init.body === "string" && !headers.has("content-type")) headers.set("content-type", "application/json");

  const response = await fetch(`/api/v1${path}`, {
    ...init,
    headers,
    credentials: "same-origin"
  });
  const body = (await response.json().catch(() => ({}))) as Envelope<T> & ApiErrorPayload;

  if (!response.ok) {
    const message = body.error?.message || `Request failed (${response.status})`;
    throw new ApiRequestError(message, response.status, body.error?.code);
  }
  return body.data;
}

async function download(path: string): Promise<Blob> {
  const response = await fetch(`/api/v1${path}`, { credentials: "same-origin" });
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as ApiErrorPayload;
    throw new ApiRequestError(body.error?.message || `Request failed (${response.status})`, response.status, body.error?.code);
  }
  return response.blob();
}

export const api = {
  auth: {
    status: () => request<{ user: User | null }>("/auth/status"),
    login: (input: { email: string; password: string }) => request<{ user: User }>("/auth/login", { method: "POST", body: JSON.stringify(input) }),
    register: (input: { name: string; email: string; password: string }) => request<{ user: User }>("/auth/register", { method: "POST", body: JSON.stringify(input) }),
    logout: () => request<{ logged_out: boolean }>("/auth/logout", { method: "POST" })
  },
  creative: {
    aiCapabilities: () => request<CreativeAiCapabilities>("/creative/ai/capabilities"),
    listProjects: (filters: { kind?: CreativeProjectKind; limit?: number } = {}) => {
      const query = new URLSearchParams();
      if (filters.kind) query.set("kind", filters.kind);
      if (filters.limit) query.set("limit", String(filters.limit));
      return request<{ projects: CreativeProject[] }>(`/creative/projects${query.size ? `?${query.toString()}` : ""}`);
    },
    createProject: (input: { kind: CreativeProjectKind; title: string; initialSnapshot: CreativeSnapshot; assetIds?: string[] }) => request<{ project: CreativeProject; revision: CreativeRevision }>("/creative/projects", {
      method: "POST",
      body: JSON.stringify({ kind: input.kind, title: input.title, initial_snapshot: input.initialSnapshot, ...(input.assetIds !== undefined ? { asset_ids: input.assetIds } : {}) })
    }),
    getProject: (projectId: string) => request<{ project: CreativeProject; revision: CreativeRevision | null }>(`/creative/projects/${encodeURIComponent(projectId)}`),
    listRevisions: (projectId: string, limit = 50) => request<{ revisions: CreativeRevisionSummary[] }>(`/creative/projects/${encodeURIComponent(projectId)}/revisions?limit=${encodeURIComponent(String(limit))}`),
    getRevision: (projectId: string, revisionId: string) => request<{ revision: CreativeRevision }>(`/creative/projects/${encodeURIComponent(projectId)}/revisions/${encodeURIComponent(revisionId)}`),
    appendRevision: (projectId: string, input: { expectedRevisionNumber: number; snapshot: CreativeSnapshot; assetIds?: string[] }) => request<{ project: CreativeProject; revision: CreativeRevision }>(`/creative/projects/${encodeURIComponent(projectId)}/revisions`, {
      method: "POST",
      body: JSON.stringify({ expected_revision_number: input.expectedRevisionNumber, snapshot: input.snapshot, ...(input.assetIds !== undefined ? { asset_ids: input.assetIds } : {}) })
    }),
    listAssets: (filters: { projectId?: string; type?: string; limit?: number } = {}) => {
      const query = new URLSearchParams();
      if (filters.projectId) query.set("project_id", filters.projectId);
      if (filters.type) query.set("type", filters.type);
      if (filters.limit) query.set("limit", String(filters.limit));
      return request<{ assets: CreativeAssetDescriptor[] }>(`/creative/assets${query.size ? `?${query.toString()}` : ""}`);
    },
    uploadAsset: async (file: Blob, filename: string, projectId?: string) => {
      const form = new FormData();
      form.append("file", file, filename);
      if (projectId) form.append("project_id", projectId);
      return request<{ asset: CreativeAssetDescriptor }>("/creative/assets", { method: "POST", body: form });
    }
  },
  platformCommerce: {
    listPlans: () => request<{ plans: PlatformPlan[] }>("/commerce/plans"),
    billing: () => request<PlatformBilling>("/commerce/billing")
  },
  domains: {
    readiness: () => request<DomainReadiness & { public_dns_resolver: boolean }>("/domains/readiness"),
    list: () => request<{ domains: DomainInventoryItem[] }>("/domains"),
    tlds: () => request<{ tlds: Array<{ tld: string; registration_minor: number | null; renewal_minor: number | null; currency: string | null }>; source: string; checked_at: string }>("/domains/tlds"),
    search: (domain: string) => request<{ query: string; provider: string; results: DomainSearchResult[]; checked_at: string }>("/domains/search", { method: "POST", body: JSON.stringify({ domain }) }),
    checkAndQuote: (domain: string) => request<{ query: string; provider: string; results: DomainSearchResult[]; checked_at: string }>("/domains/check-and-quote", { method: "POST", body: JSON.stringify({ domain }) }),
    connect: (input: { domain: string; projectId: string; authorized_domain_control: true }, idempotencyKey: string) => request<DomainConnectResult>("/domains/connect", { method: "POST", headers: { "idempotency-key": idempotencyKey }, body: JSON.stringify(input) }),
    verifyControl: (domainId: string) => request<{ domain: DomainInventoryItem; hosting_ready: boolean; next_step?: string; already_verified?: boolean }>(`/domains/${encodeURIComponent(domainId)}/verify-control`, { method: "POST", body: JSON.stringify({}) }),
    dns: (domainId: string) => request<{ domain_id: string; ownership: string; challenge: { challenge_record_name: string; expires_at: string; status: string; value_available: boolean } | null; instructions: { verification: { type: string; name: string; purpose: string }; hosting: { type: string; name: string; value: string; purpose: string } | null }; hosting_ready: boolean }>(`/domains/${encodeURIComponent(domainId)}/dns`),
    attachSite: (domainId: string, projectId: string) => request<{ domain_id: string; project_id: string; status: string; hosting_ready: boolean; note: string }>(`/domains/${encodeURIComponent(domainId)}/attach-site`, { method: "POST", body: JSON.stringify({ projectId }) }),
    provisionHostname: (domainId: string, input: { authorized_cloudflare_hostname_creation: boolean }) => request<{ domain_id: string; provider: string; hostname: { status: string; ssl_status: string; validation?: { ownership?: { type: string; name: string; value: string } | null; ssl_txt_records?: Array<{ type: string; name: string; value: string; status: string }> }; certificate_expires_at?: string | null }; customer_dns: { type: string; name: string; value: string; instruction: string }; required_gates: string[]; idempotent_reuse: boolean }>(`/domains/${encodeURIComponent(domainId)}/provision-hostname`, { method: "POST", body: JSON.stringify(input) }),
    createOrder: (input: Record<string, unknown>) => request<{ order: { id: string; status: string } }>("/domains/orders", { method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(input) }),
    redeemEntitlement: (input: Record<string, unknown>) => request<{ order: { id: string; status: string } }>("/domains/entitlements/redeem", { method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(input) }),
    providerAdminConfig: () => request<DomainProviderAdminConfig>("/domains/admin/provider-config/openprovider"),
    saveProviderAdminConfig: (input: { expected_version: number; username: string; password: string; sandbox_v1_evidence_ref: string; no_charge_evidence_ref: string; price_operations_evidence_ref: string }) => request<DomainProviderAdminConfig>("/domains/admin/provider-config/openprovider", { method: "PUT", body: JSON.stringify(input) }),
    clearProviderAdminCredentials: (expected_version: number) => request<DomainProviderAdminConfig>("/domains/admin/provider-config/openprovider/clear", { method: "POST", body: JSON.stringify({ expected_version, confirm_clear: true }) })
  },
  themes: {
    list: (query = "") => request<Theme[]>(`/themes${query ? `?q=${encodeURIComponent(query)}` : ""}`)
  },
  templates: {
    list: (query = "", filters: { category?: string; capabilities?: string[]; includeDrafts?: boolean } = {}) => {
      const params = new URLSearchParams({ limit: "100" });
      if (query) params.set("q", query);
      if (filters.category) params.set("category", filters.category);
      if (filters.capabilities?.length) params.set("capabilities", filters.capabilities.join(","));
      if (filters.includeDrafts) params.set("includeDrafts", "true");
      return request<{ templates: PremiumTemplate[]; total: number; categories: Array<{ slug: string; count: number }>; capabilities: Array<{ slug: string; count: number }> }>(`/templates?${params.toString()}`);
    }
  },
  projects: {
    list: () => request<{ projects: Project[] }>("/projects"),
    get: (projectId: string) => request<{ project: Project }>(`/projects/${projectId}`),
    updateProject: (projectId: string, input: { name: string }) => request<{ project: Project }>(`/projects/${encodeURIComponent(projectId)}`, { method: "PATCH", body: JSON.stringify(input) }),
    deleteProject: (projectId: string) => request<{ deleted: boolean; soft_deleted: boolean; public_site_hidden: boolean }>(`/projects/${encodeURIComponent(projectId)}`, { method: "DELETE" }),
    resources: (projectId: string) => request<{ bindings: ProjectResourceBinding[]; active: ProjectResourceBinding[] }>(`/projects/${encodeURIComponent(projectId)}/resources`),
    connectResources: (projectId: string, input: ConnectProjectResourcesInput) => request<{ connected: boolean; project_id: string; binding_count: number; products: Array<{ id: string; name: string; sku: string; price_amount: number; currency: string; variants: number }> }>(`/projects/${encodeURIComponent(projectId)}/resources`, { method: "POST", body: JSON.stringify(input) }),
    disconnectResource: (projectId: string, type: ProjectResourceBinding["type"]) => request<{ disconnected: boolean; type: string; note: string }>(`/projects/${encodeURIComponent(projectId)}/resources/${encodeURIComponent(type)}`, { method: "DELETE" }),
    create: (input: { name: string; slug?: string }) => request<{ project: Project }>("/projects", { method: "POST", body: JSON.stringify(input) }),
    createFromTemplate: (input: { templateId: string; name?: string; slug?: string }) => request<{ project: Project; template: { id: string; slug: string; version: string }; revision: { number: number; source_template_version_id: string } }>("/projects/from-template", { method: "POST", body: JSON.stringify(input) }),
    generate: (projectId: string, input: { prompt: string; themeId?: string; idempotencyKey?: string }) => request<{ job: Generation; idempotent_replay?: boolean }>(`/projects/${projectId}/generations`, { method: "POST", body: JSON.stringify(input) }),
    latestGeneration: (projectId: string) => request<{ job: Generation | null }>(`/projects/${projectId}/generations/latest`),
    generationEvents: (projectId: string, jobId: string) => request<{ job: Generation; steps: GenerationStep[] }>(`/projects/${projectId}/generations/${jobId}/events`),
    pages: (projectId: string) => request<{ pages: ProjectPage[] }>(`/projects/${projectId}/pages`),
    products: (projectId: string, params: { q?: string; status?: string; category?: string; page?: number; limit?: number } = {}) => {
      const query = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== "") query.set(key, String(value));
      return request<{ products: CatalogProduct[]; categories: string[]; pagination: { page: number; pageSize: number; total: number; hasMore: boolean } }>(`/projects/${encodeURIComponent(projectId)}/products${query.size ? `?${query.toString()}` : ""}`);
    },
    seedDefaultProductSamples: (projectId: string) => request<{ sample_count: number; draft_only: boolean; idempotent_replay?: boolean }>(`/projects/${encodeURIComponent(projectId)}/products/sample-set`, { method: "POST", body: JSON.stringify({}) }),
    createProduct: (projectId: string, input: CatalogProductInput) => request<{ product: CatalogProduct }>(`/projects/${encodeURIComponent(projectId)}/products`, { method: "POST", body: JSON.stringify(input) }),
    updateProduct: (projectId: string, productId: string, input: Partial<CatalogProductInput>) => request<{ product: CatalogProduct }>(`/projects/${encodeURIComponent(projectId)}/products/${encodeURIComponent(productId)}`, { method: "PATCH", body: JSON.stringify(input) }),
    deleteProduct: (projectId: string, productId: string) => request<{ deleted: boolean }>(`/projects/${encodeURIComponent(projectId)}/products/${encodeURIComponent(productId)}`, { method: "DELETE" }),
    exportProductsShopify: (projectId: string) => download(`/projects/${encodeURIComponent(projectId)}/products/export/shopify.csv`),
    exportProductsWooCommerce: (projectId: string) => download(`/projects/${encodeURIComponent(projectId)}/products/export/woocommerce.csv`),
    uploadProductImage: (projectId: string, productId: string, image: File) => {
      const form = new FormData();
      form.append("image", image, image.name);
      return request<{ product: CatalogProduct }>(`/projects/${encodeURIComponent(projectId)}/products/${encodeURIComponent(productId)}/image`, { method: "POST", body: form });
    },
    importProductsCsv: (projectId: string, csv: File, images: File[] = []) => {
      const form = new FormData();
      form.append("file", csv, csv.name);
      for (const image of images) form.append("images", image, image.name);
      return request<{ createdCount: number; missingImages: Array<{ filename: string; row: number }>; unusedImages: string[]; status: "draft"; format?: "shopify"; sourceRowCount?: number }>(`/projects/${encodeURIComponent(projectId)}/products/import`, { method: "POST", body: form });
    },
    pageRevisions: (projectId: string, pageId: string) => request<{ revisions: ProjectPageRevision[] }>(`/projects/${projectId}/pages/${pageId}/revisions`),
    addProductToPage: (projectId: string, pageId: string, input: { productId: string; expectedVersion: number }) => request<{ added: boolean; page: ProjectPage; product: { id: string; name: string; sku: string }; site_version_id?: string; revision_number: number; page_version: number }>(`/projects/${encodeURIComponent(projectId)}/pages/${encodeURIComponent(pageId)}/products`, { method: "POST", body: JSON.stringify(input) }),
    updatePage: (projectId: string, pageId: string, input: { schema: Record<string, unknown>; title?: string; slug?: string; expectedVersion: number }) => request<{ page: ProjectPage; version: number; page_version: number }>(`/projects/${projectId}/pages/${pageId}`, { method: "PATCH", body: JSON.stringify(input) }),
    messages: (projectId: string) => request<{ messages: ChatMessage[] }>(`/projects/${projectId}/chat/messages`),
    sendMessage: (projectId: string, message: string, idempotencyKey: string) => request<{ message: string; version: number; project_revision?: number | null; operations: unknown[]; provider: string; ai_gateway_log_id?: string | null; context_messages_used?: number; preview?: ProjectPreview; verification?: { ok: boolean; checks: Array<{ operation: string; verified: boolean }> } | null; catalogQuery?: string; catalogSuggestions?: CatalogProduct[]; command_id?: string; idempotent_replay?: boolean }>(`/projects/${encodeURIComponent(projectId)}/chat/messages`, { method: "POST", headers: { "Idempotency-Key": idempotencyKey }, body: JSON.stringify({ message }) }),
    createPreview: (projectId: string) => request<ProjectPreview>(`/projects/${projectId}/previews`, { method: "POST", body: JSON.stringify({ mode: "v3-workbench" }) }),
    publish: (projectId: string) => request<{ deployment: { id: string; status: string; public_url: string; artifact_sha256: string } }>(`/projects/${projectId}/deployments`, { method: "POST", body: JSON.stringify({}) })
  },
  commerce: {
    store: (projectId: string) => request<{ store: CommerceStore }>(`/projects/${encodeURIComponent(projectId)}/store`),
    provisionStore: (projectId: string, input: { name?: string }, idempotencyKey: string) => request<{ store: CommerceStore; idempotent_replay?: boolean }>(`/projects/${encodeURIComponent(projectId)}/store`, { method: "POST", headers: { "idempotency-key": idempotencyKey }, body: JSON.stringify(input) }),
    products: (projectId: string) => request<{ products: StoreCommerceProduct[] }>(`/projects/${encodeURIComponent(projectId)}/store/products`),
    inventory: (projectId: string) => request<{ inventory: StoreInventoryLevel[] }>(`/projects/${encodeURIComponent(projectId)}/store/inventory`),
    orders: (projectId: string) => request<{ orders: StoreOrder[] }>(`/projects/${encodeURIComponent(projectId)}/store/orders`),
    customers: (projectId: string) => request<{ customers: StoreCustomer[] }>(`/projects/${encodeURIComponent(projectId)}/store/customers`),
    shippingZones: (projectId: string) => request<{ zones: StoreShippingZone[] }>(`/projects/${encodeURIComponent(projectId)}/store/shipping/zones`),
    paymentProviders: (projectId: string) => request<{ providers: StorePaymentProvider[] }>(`/projects/${encodeURIComponent(projectId)}/store/payment-providers`),
    analytics: (projectId: string, from = "1970-01-01", to = "9999-12-31") => request<{ range: { from: string; to: string }; metrics: StoreAnalytics }>(`/projects/${encodeURIComponent(projectId)}/store/analytics?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
    setup: (projectId: string) => request<{ store: CommerceStore; setup: StoreSetup | null; readiness: { ready: boolean; missing_steps: string[]; optional_steps: string[] } }>(`/projects/${encodeURIComponent(projectId)}/store/setup`),
    createProduct: (projectId: string, input: { name: string; sku: string; price_amount_minor: number }, idempotencyKey: string) => request<{ product: StoreCommerceProduct }>(`/projects/${encodeURIComponent(projectId)}/store/products`, { method: "POST", headers: { "idempotency-key": idempotencyKey }, body: JSON.stringify(input) })
  },
  socialCommerce: {
    status: (projectId: string) => request<{ enabled: boolean; facebook: { status: string; authorization: string; oauth_ready: boolean; redirect_uri: string; pages: FacebookPageConnection[] }; profile_import: { mode: string; automatic_api_allowed_for_commercial_use: boolean }; manual_import: { available: boolean }; workers_ai: boolean; blockers: string[]; commerce_enabled: boolean }>(`/projects/${encodeURIComponent(projectId)}/social`),
    connectFacebookPage: (projectId: string) => request<{ authorization_url: string; redirect_uri: string }>(`/projects/${encodeURIComponent(projectId)}/social/facebook/connect`, { method: "POST", body: JSON.stringify({}) }),
    facebookPages: (projectId: string) => request<{ pages: FacebookPageConnection[] }>(`/projects/${encodeURIComponent(projectId)}/social/facebook/pages`),
    pendingFacebookPages: (projectId: string, pendingId: string) => request<{ pages: FacebookPendingPage[] }>(`/projects/${encodeURIComponent(projectId)}/social/facebook/pending/${encodeURIComponent(pendingId)}`),
    selectFacebookPage: (projectId: string, pendingId: string, pageId: string) => request<{ connected: boolean; page: FacebookPageConnection }>(`/projects/${encodeURIComponent(projectId)}/social/facebook/pending/${encodeURIComponent(pendingId)}/select`, { method: "POST", body: JSON.stringify({ page_id: pageId }) }),
    facebookPosts: (projectId: string, pageId = "") => request<{ posts: FacebookImportedPost[]; pages: Array<{ page_id: string; page_name: string; has_more: boolean }> }>(`/projects/${encodeURIComponent(projectId)}/social/facebook/posts${pageId ? `?page_id=${encodeURIComponent(pageId)}` : ""}`),
    syncFacebookPosts: (projectId: string, pageId: string, mode: "latest" | "next") => request<{ synced: number; returned: number; copied_images: number; skipped_images: number; skipped_posts: number; has_more: boolean; mode: string }>(`/projects/${encodeURIComponent(projectId)}/social/facebook/posts/sync`, { method: "POST", body: JSON.stringify({ page_id: pageId, mode, authorized_data_confirmed: true }) }),
    convertFacebookPost: (projectId: string, postId: string) => request<{ already_imported: boolean; batch: { id: string; status?: string; source_type?: string; item_count?: number }; candidate_count: number | null; media_count?: number; note?: string }>(`/projects/${encodeURIComponent(projectId)}/social/facebook/posts/${encodeURIComponent(postId)}/convert`, { method: "POST", body: JSON.stringify({ authorized_data_confirmed: true }) }),
    batches: (projectId: string) => request<{ batches: SocialImportBatch[] }>(`/projects/${encodeURIComponent(projectId)}/social/imports`),
    importText: (projectId: string, items: Array<{ source_key?: string; text: string }>) => request<{ batch: SocialImportBatch & { processed_count: number; candidate_count: number; skipped_count: number } }>(`/projects/${encodeURIComponent(projectId)}/social/imports`, { method: "POST", body: JSON.stringify({ source_type: "merchant_supplied_text", authorized_data_confirmed: true, items }) }),
    candidates: (projectId: string, batchId: string) => request<{ batch: SocialImportBatch; candidates: SocialProductCandidate[] }>(`/projects/${encodeURIComponent(projectId)}/social/imports/${encodeURIComponent(batchId)}/candidates`),
    review: (projectId: string, candidateId: string, input: { review_status: "approved" | "skipped"; title?: string; description?: string; category?: string; price_amount_minor?: number | null; currency?: string }) => request<{ updated: boolean; review_status: string }>(`/projects/${encodeURIComponent(projectId)}/social/candidates/${encodeURIComponent(candidateId)}`, { method: "PATCH", body: JSON.stringify(input) }),
    createProducts: (projectId: string, batchId: string, candidateIds: string[]) => request<{ created: number; products: Array<{ candidate_id: string; product_id: string; sku: string; status: string }>; note: string }>(`/projects/${encodeURIComponent(projectId)}/social/imports/${encodeURIComponent(batchId)}/create-store`, { method: "POST", body: JSON.stringify({ candidate_ids: candidateIds }) })
  }
};

export type CommerceStore = { id: string; project_id: string; slug: string; name: string; status: string; currency: string; locale: string; timezone: string; storefront_status: string };
export type StoreCommerceProduct = { id: string; sku: string; slug: string; name: string; category?: string; status: string; price_amount_minor: number; currency: string; stock_quantity?: number };
export type StoreInventoryLevel = { id: string; variant_sku: string; location_name: string; available_quantity: number; reserved_quantity: number; sellable_quantity: number };
export type StoreOrder = { id: string; order_number: string; status: string; payment_status: string; fulfillment_status: string; currency: string; total_minor: number; created_at: string };
export type StoreCustomer = { id: string; status: string; created_at: string };
export type StoreShippingZone = { id: string; name: string; status: string; countries: string[]; provinces: string[] };
export type StorePaymentProvider = { id: string; provider: string; mode: string; status: string; provider_account_masked?: string | null };
export type StoreAnalytics = { orders_total: number; paid_orders: number; gross_paid_minor: number; refunded_minor: number; net_paid_minor: number; refund_count: number };
export type StoreSetup = { id: string; setup_status: string; completed_steps: string[]; contact_configured: boolean; shipping_origin_configured: boolean; domain?: string | null };
export type SocialImportBatch = { id: string; source_type: string; status: string; item_count: number; error_code?: string | null; created_at?: string; processed_count?: number; candidate_count?: number; skipped_count?: number };
export type SocialMediaRef = { content_type: string; byte_size: number; alt_text: string; url: string };
export type FacebookImportedPost = { id: string; page_id: string; page_name: string; post_id: string; source_text: string; permalink_url: string | null; source_created_at: string | null; fetched_at: string; status: "available" | "imported" | "skipped"; media: SocialMediaRef[] };
export type SocialProductCandidate = { id: string; title: string; description: string; category: string | null; price_amount_minor: number | null; currency: string | null; inventory_quantity: number | null; confidence_by_field: Record<string, unknown>; missing_fields: string[]; policy_flags: string[]; source_excerpt: string; source_text: string; review_status: string; product_id: string | null; media?: SocialMediaRef[] };
export type FacebookPageConnection = { page_id: string; page_name: string; status?: string; connected_at?: string; tasks?: string[] };
export type FacebookPendingPage = { page_id: string; page_name: string; tasks: string[] };
